// SHA-256 해시 블랙리스트 관리 페이지 — 등록/조회/삭제
import { useEffect, useState } from 'react';
import { adminGet, adminPost, adminDelete } from '../lib/adminApi';
import { useUI } from '../lib/uiStore';

interface HashEntry {
  sha256: string;
  threatLabel: string | null;
  source: string | null;
  addedAt: string;
}

export function HashBlacklist() {
  const [entries, setEntries] = useState<HashEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [sha256, setSha256] = useState('');
  const [label, setLabel] = useState('');
  const [source, setSource] = useState('');
  const [adding, setAdding] = useState(false);
  const { showToast } = useUI();

  async function load() {
    setLoading(true);
    try {
      const d = await adminGet<{ items: HashEntry[] }>('/admin/hash-blacklist');
      setEntries(d.items);
    } catch {
      showToast('목록 로드 실패', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[a-f0-9]{64}$/.test(sha256.trim())) {
      showToast('유효한 SHA-256 해시를 입력하세요 (64자리 16진수)', 'error');
      return;
    }
    setAdding(true);
    try {
      await adminPost('/admin/hash-blacklist', {
        sha256: sha256.trim(),
        threatLabel: label.trim() || undefined,
        source: source.trim() || undefined,
      });
      showToast('해시 등록 완료', 'success');
      setSha256(''); setLabel(''); setSource('');
      await load();
    } catch {
      showToast('등록 실패', 'error');
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(hash: string) {
    if (!confirm(`${hash.slice(0, 16)}... 을 블랙리스트에서 삭제하시겠습니까?`)) return;
    try {
      await adminDelete(`/admin/hash-blacklist/${hash}`);
      showToast('삭제 완료', 'success');
      setEntries((prev) => prev.filter((e) => e.sha256 !== hash));
    } catch {
      showToast('삭제 실패', 'error');
    }
  }

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">🚫 해시 블랙리스트 <em>SHA-256 등록</em></div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-head"><div className="card-head-title">새 해시 등록</div></div>
        <form onSubmit={(e) => void handleAdd(e)} className="card-body">
          <div className="form-row">
            <label className="form-label" htmlFor="hash-sha256">SHA-256 해시</label>
            <input
              id="hash-sha256"
              className="form-input mono"
              placeholder="64자리 16진수 (예: a1b2c3d4...)"
              value={sha256}
              onChange={(e) => setSha256(e.target.value)}
              required
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-row" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="hash-label">위협 레이블</label>
              <input
                id="hash-label"
                className="form-input"
                placeholder="예: Trojan.GenericKD"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="form-row" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="hash-source">출처</label>
              <input
                id="hash-source"
                className="form-input"
                placeholder="예: VirusTotal, internal"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <button type="submit" className="btn btn-danger" disabled={adding}>
              {adding ? '등록 중...' : '🚫 차단 등록'}
            </button>
          </div>
        </form>
      </div>

      <div className="sect-head">
        <div className="sect-title">📋 차단 목록 <em>{entries.length}개</em></div>
        <button className="btn btn-ghost btn-sm" onClick={() => void load()}>새로고침</button>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>로딩 중...</div>
      ) : entries.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>
          등록된 해시 없음. 위 양식에서 SHA-256 해시를 추가하세요.
        </div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>SHA-256</th>
                <th>위협 레이블</th>
                <th>출처</th>
                <th>등록일</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={e.sha256} style={{ borderBottom: i < entries.length - 1 ? '1px solid var(--border)' : undefined }}>
                  <td>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--red)' }}>
                      {e.sha256.slice(0, 16)}...{e.sha256.slice(-8)}
                    </span>
                    <span
                      title={e.sha256}
                      style={{ marginLeft: 6, cursor: 'pointer', color: 'var(--text3)' }}
                      onClick={() => { void navigator.clipboard.writeText(e.sha256); showToast('복사됨', 'success'); }}
                    >📋</span>
                  </td>
                  <td>{e.threatLabel ?? <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                  <td style={{ color: 'var(--text2)' }}>{e.source ?? <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                  <td className="text-mono text-muted">{new Date(e.addedAt).toLocaleString('ko-KR')}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => void handleDelete(e.sha256)}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card mt-20" style={{ padding: '14px 20px' }}>
        <div style={{ color: 'var(--text2)', lineHeight: 1.8 }}>
          <strong>해시 블랙리스트란.</strong> 등록된 SHA-256 해시를 가진 파일은 ClamAV/YARA 검사 없이 즉시 격리됩니다.
          VirusTotal, 내부 분석 등에서 확보한 악성 파일 해시를 등록하여 재업로드를 차단하세요.
        </div>
      </div>
    </>
  );
}
